// Guided setup, step 5: Equipment. Embeds the existing equipment checkout editor (the SAME guarded draft -> generate & assign workflow; one issuance,
// the employee signs), or records that no equipment is needed. "No equipment needed" is a review statement only, offered in the shell footer only when nothing is
// saved (no draft, no issued checkout) and the list loaded successfully; it never clears, returns or bypasses anything. A saved DRAFT is not issued equipment and does
// not resolve this step.
import { useEffect, useRef, useState } from 'react';
import { EquipmentCheckoutPanel, type EquipmentPanelState } from '../workspace/EquipmentCheckoutPanel';
import { useHrAccess } from '../HrAccessContext';
import { StepCard, UnavailableNotice } from './stepUi';
import type { StepContext } from './setupTypes';

const HELP_BODY = (
  <>
    <p>Choose <strong>New equipment checkout</strong>, list each item (type and description are required), then <strong>Save draft</strong>. You can close a draft and come back to it.</p>
    <p className="mt-2"><strong>Generate &amp; assign</strong> freezes the list and gives the employee one form to sign. A saved draft is not issued equipment.</p>
    <p className="mt-2">Only choose “No equipment needed” when nothing is saved. It records a review only and can be undone.</p>
  </>
);

export function EquipmentStep({ ctx }: { ctx: StepContext }) {
  const e = ctx.view.summary.equipment;
  const { state: access } = useHrAccess();
  const callerEmployeeId = access.kind === 'ready' ? access.me.employeeId : null;
  const savedDecided = ctx.view.progress?.decisions.equipment?.decision === 'no_equipment_needed';
  const available = e.state !== 'unavailable';
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const [panel, setPanel] = useState<EquipmentPanelState>({ list: 'loading', count: 0, editing: false, dirty: false });

  const serverAllows = ctx.view.summary.noneNeeded?.equipment.allowed ?? (e.state === 'available' && e.checkouts.length === 0);
  const hasRecords = (e.state === 'available' && e.checkouts.length > 0) || panel.count > 0;
  // Offered only when the area is readable, the list LOADED successfully and holds nothing (no draft, no issued record), and no editor is open.
  const ackOffered = available && serverAllows && panel.list === 'ready' && panel.count === 0 && !panel.editing;
  const [ackChecked, setAckChecked] = useState(savedDecided);
  const ackRef = useRef(ackChecked); ackRef.current = ackChecked;
  const savedRef = useRef(savedDecided); savedRef.current = savedDecided;
  const offeredRef = useRef(ackOffered); offeredRef.current = ackOffered;
  const hasRecordsRef = useRef(hasRecords); hasRecordsRef.current = hasRecords;
  useEffect(() => { if (hasRecords) setAckChecked(false); }, [hasRecords]);

  useEffect(() => {
    if (!available) { ctx.bindSaver(null); ctx.setDirty(false); return; }
    ctx.bindSaver(async () => {
      if (hasRecordsRef.current) return savedRef.current ? { ok: true, decisions: { equipment: null } } : { ok: true }; // a stale “no equipment needed” beside a real record is cleared; no record is touched
      if (!offeredRef.current) return { ok: true };
      if (ackRef.current && !savedRef.current) return { ok: true, decisions: { equipment: { decision: 'no_equipment_needed' } } };
      if (!ackRef.current && savedRef.current) return { ok: true, decisions: { equipment: null } }; // deliberate undo; no record is touched
      return { ok: true };
    });
    return () => ctx.bindSaver(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  // Unsaved entries in the open editor are never dropped or claimed as saved: the shell is told, and the footer says what to do.
  useEffect(() => { ctxRef.current.setDirty(panel.dirty); }, [panel.dirty]);
  const invalidMessage = panel.dirty ? 'The equipment draft has changes that are not saved. Choose Save draft (or Close draft) before leaving this step.' : null;
  useEffect(() => {
    ctxRef.current.bindFooter({
      ...(ackOffered ? { ack: { id: 'equipment-none-needed', label: 'No equipment needed', checked: ackChecked, onChange: setAckChecked } } : {}),
      help: { title: 'How equipment checkout works', body: HELP_BODY },
      invalidMessage,
    });
  }, [ackOffered, ackChecked, invalidMessage]);
  useEffect(() => () => ctxRef.current.bindFooter(null), []);

  if (e.state === 'unavailable') {
    return (
      <StepCard title="Equipment" subtitle="Record the equipment this employee will receive." testId="step-equipment">
        <UnavailableNotice title="Equipment" reason={e.reason} capability={e.capability} />
      </StepCard>
    );
  }
  return (
    <div data-testid="step-equipment">
      <EquipmentCheckoutPanel
        employeeId={ctx.employeeId} callerEmployeeId={callerEmployeeId}
        title="Equipment" subtitle="Record the equipment this employee will receive. Generating a checkout gives the employee one form to sign." collapsible={false}
        onAssigned={() => { void ctxRef.current.reload(); ctxRef.current.reloadSoon(); }}
        onSaved={() => { void ctxRef.current.reload(); }}
        onStateChange={setPanel}
      />
    </div>
  );
}
